import { AccessControlProvider } from "@refinedev/core";
import { gqlClient } from "../helpers/gqlProvider";
import { MY_PERMISSIONS_QUERY } from "../pages/permissions/queries";

interface Permission {
  organizationalRoles: string[];
  resources: string[];
  actions: string[];
}

const parentChildMap: Record<string, string[]> = {
  procurement: [
    "procurementPlans",
    "purchaseRequests",
    "procurementPlanReports",
  ],
  execution: ["purchaseOrders", "sourcings", "receivings"],
  templates: ["purchaseTemplates", "documentTemplates", "minuteTemplates"],
  finance: ["invoices", "paymentRequests"],
  configuration: [
    "users",
    "teams",
    "vendors",
    "defaultCommittees",
    "permissions",
  ],
};

let cachedPermissions: Permission[] | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 1000 * 60 * 15;

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource, action }) => {
    const now = Date.now();

    if (!cachedPermissions || (now - cacheTimestamp) > CACHE_DURATION) {
      const result = await gqlClient.query(MY_PERMISSIONS_QUERY, {}).toPromise();
      cachedPermissions = result.data?.myPermissions as Permission[];
      cacheTimestamp = now;
    }

    const isAllowed = cachedPermissions.some((p) => {
      const hasResourceAccess =
        p.resources?.includes("*") ||
        p.resources?.includes(resource) ||
        (parentChildMap[resource] &&
          parentChildMap[resource].some((child) =>
            p.resources?.includes(child)
          ));

      const hasActionAccess =
        p.actions?.includes("*") || p.actions?.includes(action);

      return hasResourceAccess && hasActionAccess;
    });

    return { can: isAllowed };
  },
};

export function clearPermissionsCache() {
  cachedPermissions = null;
  cacheTimestamp = 0;
}
